/**
 * The pure week helpers the football strip is built from: the labels a fan
 * reads and the date-to-week lookup the calendar needs. ISSUE-950 R6/R9.
 *
 * A week is a season week as `POST /event/weeks-with-games-list` returns it:
 * the vendor's own week number plus the first and last day it has a game on,
 * already rendered in the fan's time zone by events-api. Nothing here talks to
 * the network or to React, so the strip's rules can be tested on their own.
 */
import { longDayLabel } from "./calendarDay";

/** The one sport whose season runs in weeks. This is the whole rule (ISSUE-950
 *  R3): a league is week based when its catalog SPORT carries this key, so a
 *  football league added to the catalog later gets week chips with no code
 *  change here. Never match on a league name or a league key. */
export const WEEK_BASED_SPORT_KEY = "football";

/** One week of a league's season, as the weeks read returns it. `firstDay`
 *  and `lastDay` are "YYYY-MM-DD" in the fan's time zone, inclusive. */
export type typeSeasonWeek = {
  seasonWeek: number;
  firstDay: string;
  lastDay: string;
};

/** The two words a week's headings and empty line start with: "Week 3". */
export function weekLabel(seasonWeek: number): string {
  return `Week ${String(seasonWeek)}`;
}

/** The full sentence a screen reader hears and the desktop heading shows:
 *  "Week 3, Sunday, Sept 13 to Monday, Sept 21". The word "to", never a
 *  dash, per human-writing.mdc. A one-day week says its day once. */
export function weekRangeLabel(week: typeSeasonWeek): string {
  const first = longDayLabel(week.firstDay);
  if (week.firstDay === week.lastDay) {
    return `${weekLabel(week.seasonWeek)}, ${first}`;
  }
  return `${weekLabel(week.seasonWeek)}, ${first} to ${longDayLabel(week.lastDay)}`;
}

/** The week holding `day`, or null when the day falls in a gap between two
 *  weeks. This is what turns a calendar pick, and an already-shared `?date=`
 *  link, into a week selection. */
export function findWeekContainingDay(input: {
  weeks: readonly typeSeasonWeek[];
  day: string;
}): typeSeasonWeek | null {
  return (
    input.weeks.find(
      (week) => week.firstDay <= input.day && input.day <= week.lastDay,
    ) ?? null
  );
}
